import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import styles from "./Shell.module.css";
import { OnboardingBanner } from "../features/onboarding/Banner.js";
import { Logo } from "../ui/index.js";

/**
 * Mobile-first, and genuinely so: below 640px this is a single column with a
 * bottom tab bar, because that is where a thumb is.
 *
 * Above it the same `<nav>` is restyled rather than replaced — a bar along the
 * top between 640px and 1024px, a persistent left rail above that. A window
 * half a screen wide is not a phone and should not be handed a thumb bar.
 */

interface Destination {
  to: string;
  label: string;
  icon: ReactNode;
  /**
   * Carried by the wide nav only, beneath the destination they belong to.
   * Above 640px there is room for real navigation rather than a stretched
   * phone layout; on a phone these are reached from the screens above them.
   */
  children?: Destination[];
}

/**
 * What the bottom tab bar carries. Six is the most a 375px bar holds with
 * 44px targets intact, so this list is the things opened weekly rather than
 * everything the app has.
 */
const PRIMARY: Destination[] = [
  { to: "/", label: "Dashboard", icon: <IconHome /> },
  {
    to: "/transactions",
    label: "Transactions",
    icon: <IconList />,
    children: [
      { to: "/import", label: "Import", icon: <IconUpload /> },
      { to: "/transfers", label: "Transfers", icon: <IconSwap /> },
      { to: "/categories", label: "Categories", icon: <IconTag /> },
      { to: "/rules", label: "Rules", icon: <IconFilter /> },
      { to: "/duplicates", label: "Duplicates", icon: <IconCopy /> },
      { to: "/review", label: "Review", icon: <IconFlag /> },
    ],
  },
  { to: "/ask", label: "Ask", icon: <IconAsk /> },
  { to: "/insights", label: "Insights", icon: <IconSpark /> },
  {
    to: "/accounts",
    label: "Accounts",
    icon: <IconWallet />,
    children: [{ to: "/budgets", label: "Budgets", icon: <IconTarget /> }],
  },
  {
    to: "/settings",
    label: "Settings",
    icon: <IconCog />,
    children: [{ to: "/history", label: "History", icon: <IconClock /> }],
  },
];

export function Shell() {
  const path = useRouterState({ select: (state) => state.location.pathname });

  const isCurrent = (to: string) => (to === "/" ? path === "/" : path.startsWith(to));

  const sideLink = (destination: Destination, nested = false) => (
    <Link
      to={destination.to}
      className={nested ? `${styles.sideLink} ${styles.subLink}` : styles.sideLink}
      aria-current={isCurrent(destination.to) ? "page" : undefined}
    >
      <span className={styles.icon}>{destination.icon}</span>
      {destination.label}
    </Link>
  );

  const sideItem = (destination: Destination) => (
    <li key={destination.to}>
      {sideLink(destination)}
      {destination.children && (
        <ul className={styles.subList}>
          {destination.children.map((child) => (
            <li key={child.to}>{sideLink(child, true)}</li>
          ))}
        </ul>
      )}
    </li>
  );

  const brand = (
    <div className={styles.brand}>
      <span className={styles.lockup}>
        <Logo className={styles.brandLogo} />
        <span className={styles.brandName}>Squirrel</span>
      </span>
      <div className={styles.strapLine}>Household finances in a nutshell</div>
    </div>
  );

  return (
    <div className={styles.shell}>
      {/* Below 640px the nav is a thumb bar at the bottom, which leaves the top
          of every screen with nothing on it saying what app this is. */}
      <header className={styles.topbar}>{brand}</header>

      <nav className={styles.sidebar} aria-label="Main">
        {brand}
        <ul className={styles.sideList}>{PRIMARY.map(sideItem)}</ul>
      </nav>

      <main className={styles.main}>
        <OnboardingBanner />
        <Outlet />
      </main>

      <nav className={styles.tabs} aria-label="Main">
        {PRIMARY.map((destination) => (
          <Link
            key={destination.to}
            to={destination.to}
            className={styles.tab}
            aria-current={isCurrent(destination.to) ? "page" : undefined}
          >
            <span className={styles.icon}>{destination.icon}</span>
            <span className={styles.tabLabel}>{destination.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}

/**
 * Inline SVG rather than an icon package: five icons is not a dependency, and
 * they inherit `currentColor` so the theme carries them.
 */
function IconHome() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3 10.5 12 3l9 7.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 9.5V20h13V9.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconList() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h10" strokeLinecap="round" />
    </svg>
  );
}

function IconUpload() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M12 16V4m0 0L8 8m4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 16v3h16v-3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconWallet() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3" y="6" width="18" height="13" rx="2.5" />
      <path d="M16 12.5h2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconAsk() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path
        d="M20 12.5a7 7 0 0 1-7 7H8l-4 2.5V17a7 7 0 0 1 3-12.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 10.5a2.5 2.5 0 1 1 3 2.4V14" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 16.6h.01" strokeLinecap="round" />
    </svg>
  );
}

function IconSpark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 15.5 9 10l3.5 3.5L20 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 6h5v5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSwap() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 8h13m0 0-4-4m4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 16H7m0 0 4 4m-4-4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 5.5A1.5 1.5 0 0 0 13.5 4h-8A1.5 1.5 0 0 0 4 5.5v8A1.5 1.5 0 0 0 5.5 15" />
    </svg>
  );
}

function IconTarget() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

function IconTag() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 11V5a1 1 0 0 1 1-1h6l8 8-7 7-8-8Z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="1.2" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 6h16l-6 7v5l-4 2v-7L4 6Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconFlag() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M5 21V4m0 0h11l-2 3.5L16 11H5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.2V12l3.2 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCog() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <circle cx="12" cy="12" r="3.2" />
      <path
        d="M12 3v2.2M12 18.8V21M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M3 12h2.2M18.8 12H21M4.9 19.1l1.6-1.6M17.5 6.5l1.6-1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
